import React, { useMemo } from 'react';

import PersonalisationSlotHeader from '../PersonalisationSlotHeader';
import { DatetimePicker } from '../DatetimePicker';
import { GoogleMapsLocationPicker } from '../GoogleMapsLocationPicker';
import { StarMapGenerator } from '../StarMapGenerator';
import { usePersonalisationContext } from '../../context/PersonalisationContext';

/**
 * PersonalisationStarMapSlot component displays date, time, and location pickers for star map personalisation.
 * All three inputs share the same slot.id and store data in a combined selection object.
 *
 * @param {Object} props - The component props
 * @param {Object} props.slot - The slot data
 * @returns {import('react').JSX.Element} The rendered component
 */
export default function PersonalisationStarMapSlot({ slot }) {
  const { updateSelections, selections } = usePersonalisationContext();

  // Get the current selection for this slot from the context.
  // The selection is stored as an object with datetime, time, location, and starMapImageUrl properties.
  const currentSelection = useMemo(() => {
    const slotSelection = selections.slots.find((s) => s.id === slot.id);

    // Parse the selection if it's a string (JSON) or use the object directly.
    if (!slotSelection?.selected) {
      return { datetime: '', time: '', location: '', locationPlace: null, starMapImageUrl: null };
    }

    if (typeof slotSelection.selected === 'string') {
      try {
        return JSON.parse(slotSelection.selected);
      } catch {
        // If parsing fails, assume it's a simple string (legacy format).
        return { datetime: '', time: '', location: slotSelection.selected, locationPlace: null, starMapImageUrl: null };
      }
    }

    // If it's already an object, use it directly.
    const selected = slotSelection.selected || {};
    // Use bracket notation to safely access properties.
    return {
      datetime: selected && typeof selected === 'object' ? selected['datetime'] || '' : '',
      time: selected && typeof selected === 'object' ? selected['time'] || '' : '',
      location: selected && typeof selected === 'object' ? selected['location'] || '' : '',
      locationPlace: selected && typeof selected === 'object' ? selected['locationPlace'] || null : null,
      starMapImageUrl: selected && typeof selected === 'object' ? selected['starMapImageUrl'] || null : null
    };
  }, [selections.slots, slot.id]);

  /**
   * Updates the selection in the context.
   * Merges new values with existing selection to preserve other fields.
   *
   * @param {Object} updates - Partial selection object with fields to update.
   */
  const handleSelectionUpdate = (updates) => {
    const updatedSelection = {
      ...currentSelection,
      ...updates
    };

    // Only save if at least one field has a value (including starMapImageUrl).
    const hasValue =
      updatedSelection.datetime ||
      updatedSelection.time ||
      updatedSelection.location ||
      updatedSelection.starMapImageUrl;

    updateSelections({
      slotId: slot.id,
      itemSelected: hasValue ? updatedSelection : null
    });
  };

  // Handler for datetime changes.
  const handleDatetimeChange = (value) => {
    handleSelectionUpdate({ datetime: value || '' });
  };

  // Handler for time changes.
  const handleTimeChange = (value) => {
    handleSelectionUpdate({ time: value || '' });
  };
  // Handler for location changes.
  // The place parameter is already the extracted/serialized data from GoogleMapsLocationPicker.
  const handleLocationChange = (address, place) => {
    // The place parameter is already extracted serializable data from gmp-place-autocomplete.
    // If it's not provided, try to read from the hidden input's data attribute.
    let locationPlaceData = place;

    if (!locationPlaceData) {
      // Fallback: read from the data attribute on the hidden input element.
      // This handles cases where the data was stored directly in the input.
      const locationInput = document.querySelector(`input[name="${slot.name}-location"]`);
      const locationPlaceAttr = locationInput ? locationInput.getAttribute('data-location-place') : null;
      if (locationInput && locationPlaceAttr) {
        try {
          locationPlaceData = JSON.parse(locationPlaceAttr);
          console.log('Read location place data from hidden input data attribute:', locationPlaceData);
        } catch (error) {
          console.error('Error parsing location place data from attribute:', error);
        }
      }
    }

    console.log('Storing location data:', { address, locationPlaceData });

    // Store both the formatted address string and the extracted place data.
    handleSelectionUpdate({
      location: address || '',
      locationPlace: locationPlaceData
    });
  };

  /**
   * Handler for when star map generation succeeds.
   * Stores the generated image URL in the context.
   *
   * @param {string} imageUrl - The generated star map image URL (data URL or regular URL)
   */
  const handleStarMapSuccess = (imageUrl) => {
    handleSelectionUpdate({
      starMapImageUrl: imageUrl
    });
  };

  return (
    <div>
      <PersonalisationSlotHeader slot={slot} processHeading={false} />

      <DatetimePicker
        id={`${slot.id}-datetime`}
        name={`${slot.name}-datetime`}
        type={'date'}
        required={slot.required}
        labelText={slot.checkboxLabel}
        dateTimeHeading={slot.checkboxHeading}
        onChange={handleDatetimeChange}
        defaultValue={currentSelection.datetime}
      />

      <DatetimePicker
        id={`${slot.id}-time`}
        type={'time'}
        name={`${slot.name}-time`}
        required={slot.required}
        labelText="Time"
        dateTimeHeading="Select the time of your special moment (defaults to midnight)"
        onChange={handleTimeChange}
        defaultValue={currentSelection.time}
      />

      <GoogleMapsLocationPicker
        id={`${slot.id}-location`}
        name={`${slot.name}-location`}
        required={slot.required}
        labelText="Location"
        locationPickerHeading="Start typing to search for your location"
        onChange={handleLocationChange}
        defaultValue={currentSelection.location}
        autocompleteOptions={{
          requestOptions: {
            types: ['geocode'] // Restrict to addresses/locations (not businesses)
          },
          componentRestrictions: {
            country: ['gb', 'us'] // Allow both United Kingdom (gb) and United States (us)
          }
        }}
      />

      <StarMapGenerator starMapData={currentSelection} autoGenerate={true} onSuccess={handleStarMapSuccess} />
    </div>
  );
}
